import { getRequest, deleteRequest, postRequest, patchRequest, PaginatedResponse } from 'features/common'
import { UserRole } from 'features/users'
import settings from 'settings'
import { DeleteUserAction, InvitationAction, RequiresJoinApprovalAction, UserRoleAction } from '../state/accountSlice'
import {
  Account,
  AccountStats,
  LdsQuotas,
  Users,
  Invitation,
  RoleMappingMethod,
  BasemapSettings,
  PalettesSettings,
  AccountResource,
  ResourcesDeleteRequest,
  ResourcesDeleteResponse,
  ResourcesTransferRequest,
  ResourcesTransferResponse,
  JoinMethod,
  WorkflowsSettings,
  SupportAccessStatus,
  DataSubscriptionsResponse,
  CatalogDatasetsResponse,
  AccountAgentSettings,
  AccountBillingInformation,
  AccountGovernanceMapsData,
  AccountGovernanceConnectionsData
} from '../types'
import { Basemap } from 'features/maps/types'

const ACCOUNT = 'account'
const DELETE_ACCOUNT = 'deleteAccount'
const ROLE = 'userRole'
const DELETE_USER = 'deleteUser'
const INVITE = 'inviteUser'
const USERS = 'users'
const PAYMENTS = 'payments'
const INVITATIONS = 'invitations'
const JOIN_REQUEST = 'join_request'
const SETTINGS = 'settings'
const BASEMAPS = 'basemaps'
const WORKSPACE_ACCOUNTS_RESOURCES = 'workspaceAccountResources'
const DATA_SUBSCRIPTIONS = 'dataSubscriptions'
const DO_DATASETS = 'doDatasets'

function baseUrlFor(request: string) {
  const { accountsApiUrl, workspaceApiUrl, spatialDataCatalogUrl } = settings

  switch (request) {
    case ACCOUNT:
    case DELETE_ACCOUNT:
      return `${accountsApiUrl}/accounts`
    case ROLE:
    case DELETE_USER:
      return `${accountsApiUrl}/users`
    case INVITE:
      return `${accountsApiUrl}/accounts/invite`
    case USERS:
      return `${accountsApiUrl}/accounts/users`
    case INVITATIONS:
      return `${accountsApiUrl}/accounts/invitations`
    case JOIN_REQUEST:
      return `${accountsApiUrl}/accounts/join_request`
    case PAYMENTS:
      return `${accountsApiUrl}/payments`
    case SETTINGS:
      return `${workspaceApiUrl}/settings`
    case BASEMAPS:
      return `${workspaceApiUrl}/basemaps`
    case WORKSPACE_ACCOUNTS_RESOURCES:
      return `${workspaceApiUrl}/accounts/resources`
    case DATA_SUBSCRIPTIONS:
      return `${accountsApiUrl}/do/subscriptions`
    case DO_DATASETS:
      return `${spatialDataCatalogUrl}/do/catalog/datasets`
    default:
      throw new Error(`Account API getRequest URL did not find a proper URL for: "${request}"`)
  }
}

export const getAccount = async (): Promise<Account> => {
  const account: Account = await getRequest(baseUrlFor(ACCOUNT))
  return account
}

export const getAccountFromWorkspace = async (): Promise<Account> => {
  const account: Account = await getRequest(`${settings.workspaceApiUrl}/accounts`)
  return account
}

export const getAccountBillingInformation = async (): Promise<AccountBillingInformation> => {
  const url = `${baseUrlFor(PAYMENTS)}/settings`
  const billingInformation = await getRequest(url)
  return billingInformation
}

export const editAccountName = async (accountName: string): Promise<Account> => {
  const account = await patchRequest(baseUrlFor(ACCOUNT), { name: accountName })
  return account
}

export const patchAccount = async (patch: {
  defaultUserRole?: UserRole
  ssoJitEnabled?: boolean
  roleMappingMethod?: RoleMappingMethod
  enforcedLoginMethod?: 'sso' | null
}): Promise<Account> => {
  const account = await patchRequest(baseUrlFor(ACCOUNT), patch)
  return account
}

export const getUsers = async (): Promise<Users[]> => {
  const users = await getRequest(baseUrlFor(USERS))
  return users
}

export const getInvitationsInProgress = async (): Promise<{ invitations: Invitation[] }> => {
  const response = await getRequest(`${baseUrlFor(INVITATIONS)}/in-progress`)
  return {
    invitations: response.invitations.map((i) => ({
      ...i,
      joinMethod: i.joinMethod === JoinMethod.Backoffice ? JoinMethod.Invitation : i.joinMethod
    }))
  }
}

export const resendInvitation = async (token: string): Promise<void> => {
  return await postRequest(`${baseUrlFor(INVITATIONS)}/re-send`, { token })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withdrawInvitation = async (token: string): Promise<any> => {
  return await deleteRequest(`${baseUrlFor(INVITE)}/${token}`)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const approveRequest = async (token: string): Promise<any> => {
  return await postRequest(`${baseUrlFor(JOIN_REQUEST)}/accept`, { token })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const declineRequest = async (token: string): Promise<any> => {
  return await postRequest(`${baseUrlFor(JOIN_REQUEST)}/decline`, { token })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deleteUser = async ({
  userId,
  resourcesReceiverUserId
}: DeleteUserAction): Promise<{ status: string }> => {
  const url = `${baseUrlFor(DELETE_USER)}/${encodeURIComponent(userId)}${
    resourcesReceiverUserId ? `?resourcesReceiverUserId=${encodeURIComponent(resourcesReceiverUserId)}` : ''
  }`

  const response = await deleteRequest(url)
  return response
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const postInvitation = async ({ email, role }: InvitationAction): Promise<any> => {
  const response = await postRequest(baseUrlFor(INVITE), { email, target_role: role })
  return response
}

export const getStats = async (): Promise<AccountStats> => {
  const { accountsApiUrl, workspaceApiUrl } = settings
  const [workspaceStats, accountStats] = await Promise.allSettled([
    getRequest(`${workspaceApiUrl}/accounts/stats`),
    getRequest(`${accountsApiUrl}/accounts/stats`)
  ])

  // We want to ensure that the workspace stats are always returned, even if the account stats are not available
  // This is because the workspace stats are used for some critical features in the UI
  if (workspaceStats.status === 'fulfilled' && accountStats.status === 'rejected') {
    return workspaceStats.value
  }

  if (workspaceStats.status === 'fulfilled' && accountStats.status === 'fulfilled') {
    return {
      ...workspaceStats.value,
      stats: {
        ...workspaceStats.value.stats,
        ...accountStats.value
      }
    }
  }

  throw new Error('Failed to fetch stats')
}

export const editRequiresJoinApproval = async ({
  accountName,
  emailDomain,
  action
}: RequiresJoinApprovalAction): Promise<Account> => {
  const account = await patchRequest(baseUrlFor(ACCOUNT), {
    name: accountName,
    email_domain: emailDomain,
    requires_join_approval: action
  })
  return account
}

export const getDomainJoinCheck = async (domain: string): Promise<boolean> => {
  const response = await getRequest(`${baseUrlFor(ACCOUNT)}/check_domain_join/${domain}`)
  return response.valid
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deleteAccount = async (id: string): Promise<any> => {
  const response = await deleteRequest(`${baseUrlFor(DELETE_ACCOUNT)}/${id}`)
  return response
}

export const updateUserRole = async ({
  userId,
  role,
  resourcesReceiverUserId
}: UserRoleAction): Promise<UserRoleAction> => {
  return await patchRequest(`${baseUrlFor(ROLE)}/${encodeURIComponent(userId)}/role`, { role, resourcesReceiverUserId })
}

export const { apiBaseUrl } = settings

export const getLdsQuotas = async (): Promise<LdsQuotas[]> => {
  const { ldsApiUrl } = settings
  return await getRequest(`${ldsApiUrl}/v3/lds/stats`)
}

export const getCustomBasemapsList = async (): Promise<Basemap[]> => {
  return await getRequest(`${baseUrlFor(BASEMAPS)}`)
}

export const deleteBasemap = async (basemapId: string): Promise<{ status }> => {
  return await deleteRequest(`${baseUrlFor(BASEMAPS)}/${basemapId}`)
}

export const createBasemap = async (payload): Promise<Omit<Basemap, 'id'>> => {
  return await postRequest(`${baseUrlFor(BASEMAPS)}`, payload)
}

export const getBasemapsSettings = async (): Promise<BasemapSettings> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/basemaps`)
}

export const getPalettesSettings = async (): Promise<PalettesSettings> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/palettes`)
}

export const updateBasemapsSettings = async (payload): Promise<Partial<BasemapSettings>> => {
  return await patchRequest(`${baseUrlFor(SETTINGS)}/basemaps`, payload)
}

export const updatePalettesSettings = async (payload): Promise<Partial<PalettesSettings>> => {
  return await patchRequest(`${baseUrlFor(SETTINGS)}/palettes`, payload)
}

export const getResources = async (params: string): Promise<PaginatedResponse<AccountResource>> => {
  return await getRequest(`${baseUrlFor(WORKSPACE_ACCOUNTS_RESOURCES)}?${params}`)
}

export const deleteResources = async (data: ResourcesDeleteRequest): Promise<ResourcesDeleteResponse> => {
  return await postRequest(`${baseUrlFor(WORKSPACE_ACCOUNTS_RESOURCES)}/delete-batch`, data)
}

export const transferResources = async (data: ResourcesTransferRequest): Promise<ResourcesTransferResponse> => {
  return await postRequest(`${baseUrlFor(WORKSPACE_ACCOUNTS_RESOURCES)}/transfer`, data)
}

export const getWorkflowsSettings = async (): Promise<WorkflowsSettings> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/workflows`)
}

export const updateWorkflowsSettings = async (payload): Promise<WorkflowsSettings> => {
  return await patchRequest(`${baseUrlFor(SETTINGS)}/workflows`, payload)
}

export const getAccountAgentSettingsV0 = async (): Promise<AccountAgentSettings> => {
  const response = await getRequest(`${baseUrlFor(SETTINGS)}/builder-gen-ai`)
  return { enabled: response.builderGenAiEnabled }
}

export const getAccountAgentSettingsV1 = async (): Promise<AccountAgentSettings> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/carto-ai`)
}

export const updateAccountAgentSettings = async (payload: AccountAgentSettings): Promise<AccountAgentSettings> => {
  const { enabled: builderGenAiEnabled, ...rest } = payload
  const response = await patchRequest(`${baseUrlFor(SETTINGS)}/builder-gen-ai`, { builderGenAiEnabled, ...rest })
  return { enabled: response.builderGenAiEnabled }
}

export const getSupportAccessStatus = async ({ accountId }): Promise<SupportAccessStatus> => {
  return await getRequest(`${baseUrlFor(ACCOUNT)}/${accountId}/carto-access`)
}

export const updateAccountGovernanceConnections = async (payload): Promise<AccountGovernanceConnectionsData> => {
  return await patchRequest(`${baseUrlFor(SETTINGS)}/connections`, payload)
}

export const updateAccountGovernanceMaps = async (payload): Promise<AccountGovernanceMapsData> => {
  // TODO: enable when the API is ready, return mock data for now
  return await patchRequest(`${baseUrlFor(SETTINGS)}/maps`, payload)
}

export const enableSupportAccess = async ({
  accountId,
  days
}: {
  accountId: string
  days: number
}): Promise<SupportAccessStatus> => {
  return await postRequest(`${baseUrlFor(ACCOUNT)}/${accountId}/carto-access/enable`, { days })
}

export const revokeSupportAccess = async ({
  accountId,
  targetUserId
}: {
  accountId: string
  targetUserId: string
}): Promise<{ status }> => {
  return await postRequest(`${baseUrlFor(ACCOUNT)}/${accountId}/carto-access/revoke`, { targetUserId })
}

export const getSubscriptions = async (): Promise<DataSubscriptionsResponse> => {
  return await getRequest(`${baseUrlFor(DATA_SUBSCRIPTIONS)}`)
}

export const getDODatasets = async (datasetIds): Promise<CatalogDatasetsResponse> => {
  return await postRequest(`${baseUrlFor(DO_DATASETS)}`, { datasets: datasetIds, limit: 0 })
}

export const getAccountGovernanceConnections = async (): Promise<AccountGovernanceConnectionsData> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/connections`)
}

export const getAccountGovernanceMaps = async (): Promise<AccountGovernanceMapsData> => {
  return await getRequest(`${baseUrlFor(SETTINGS)}/maps`)
}
